import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <section id="categories" className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
      <div className="text-center">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">{t('categories.title')}</h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm md:text-base text-foreground-600">{t('categories.subtitle')}</p>
      </div>

      <div className="mt-8 flex justify-center">
        <div className="relative w-full max-w-md" ref={ref}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-2 rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-900 hover:border-primary-300 transition-colors cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-apps-line text-primary-600 w-5 h-5 flex items-center justify-center" />
              {selected ? t(`cat.${selected}`) : t('categories.dropdownPlaceholder')}
            </span>
            <i className={`ri-arrow-down-s-line text-foreground-500 w-5 h-5 flex items-center justify-center transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>

          {open && (
            <div className="absolute left-0 right-0 top-full mt-2 max-h-80 overflow-y-auto rounded-xl border border-background-200 bg-background-50 py-1 z-30">
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => {
                    setSelected(c.key);
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-left text-foreground-700 hover:bg-primary-50 hover:text-primary-900 transition-colors cursor-pointer"
                >
                  <img src={c.image} alt={t(`cat.${c.key}`)} className="h-8 w-8 rounded-md object-cover object-top" />
                  <span className="flex-1 whitespace-nowrap">{t(`cat.${c.key}`)}</span>
                  <i className="ri-arrow-right-up-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        {categories.map((c) => (
          <a
            key={c.key}
            href={c.url}
            target="_blank"
            rel="noopener nofollow"
            className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 hover:border-primary-300 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md">
              <img src={c.image} alt={t(`cat.${c.key}`)} className="h-full w-full object-cover object-top" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground-900 truncate">{t(`cat.${c.key}`)}</p>
              <p className="text-xs text-foreground-500 flex items-center gap-1">
                {t('categories.visit')}
                <i className="ri-arrow-right-up-line w-3 h-3 flex items-center justify-center" />
              </p>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}